import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { BookingState, SearchCriteria, Flight, Passenger } from '../types';

export type BookingAction =
  | { type: 'SET_SEARCH_CRITERIA'; payload: SearchCriteria }
  | { type: 'SET_SELECTED_FLIGHT'; payload: Flight }
  | { type: 'SET_PASSENGER'; payload: Passenger }
  | { type: 'RESET_BOOKING' };

const initialState: BookingState = {
  searchCriteria: null,
  selectedFlight: null,
  passenger: null,
};

function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_SEARCH_CRITERIA':
      return {
        ...state,
        searchCriteria: action.payload,
      };
    case 'SET_SELECTED_FLIGHT':
      return {
        ...state,
        selectedFlight: action.payload,
      };
    case 'SET_PASSENGER':
      return {
        ...state,
        passenger: action.payload,
      };
    case 'RESET_BOOKING':
      return initialState;
    default:
      return state;
  }
}

interface BookingContextType {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(bookingReducer, initialState, (initial) => {
    try {
      const saved = localStorage.getItem('bookingState');
      if (!saved) return initial;

      const parsed = JSON.parse(saved);
      // Add basic validation
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as BookingState;
      }
      return initial;
    } catch (error) {
      console.warn('Failed to load booking state from localStorage:', error);
      return initial;
    }
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem('bookingState', JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save booking state to localStorage:', error);
    }
  }, [state]);

  return (
    <BookingContext.Provider value={{ state, dispatch }}>
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
}